import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import useEsTelefono from "../../../../utils/esTelefono";
import "./calendarioAcademico.css";

/*
	Piezas compartidas por Calendario Académico y Aulas (Horarios Claros, 2026-11-01): la ventana de alta y edición, el campo
	con su etiqueta, el «⋯» de cada fila (el mismo menú que Personal y Estudiantes) y la confirmación de borrado.
*/

export const Ventana: React.FC<{
	titulo: React.ReactNode;
	onClose: () => void;
	busy?: boolean;
	pie?: React.ReactNode;
	size?: "sm" | "lg";
	children: React.ReactNode;
}> = ({ titulo, onClose, busy, pie, size, children }) => {
	const movil = useEsTelefono();
	return (
		<Modal className="waModulo calVentana" show onHide={busy ? undefined : onClose} size={size} centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: movil ? 16 : 18 }}>{titulo}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>{children}</Modal.Body>
			{pie ? <Modal.Footer className="calPie">{pie}</Modal.Footer> : null}
		</Modal>
	);
};

/** Etiqueta arriba y el control debajo; `ancho` ocupa las dos columnas. */
export const Campo: React.FC<{ t: string; ancho?: boolean; children: React.ReactNode }> = ({ t, ancho, children }) => (
	<label className={"calCampo" + (ancho ? " ancho" : "")}><small>{t}</small>{children}</label>
);

export const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="calAvisoOro">{children}</div>;

/** El «⋯» de la fila: menú en un portal con posición fija para que la tabla con scroll no lo corte. */
export const MenuFila: React.FC<{ opciones: { texto: string; onClick: () => void; peligro?: boolean }[] }> = ({ opciones }) => {
	const [pos, setPos] = useState<React.CSSProperties | null>(null);

	useEffect(() => {
		if (!pos) return;
		const cerrar = () => setPos(null);
		window.addEventListener("scroll", cerrar, true);
		window.addEventListener("resize", cerrar);
		return () => { window.removeEventListener("scroll", cerrar, true); window.removeEventListener("resize", cerrar); };
	}, [pos]);

	if (opciones.length < 1) return null;

	const abrir = (boton: HTMLElement) => {
		if (pos) { setPos(null); return; }
		const caja = boton.getBoundingClientRect();
		const abajo = window.innerHeight - caja.bottom;
		const arriba = abajo < 160 && caja.top > abajo;
		setPos({
			position: "fixed",
			right: document.documentElement.clientWidth - caja.right,
			...(arriba ? { top: "auto", bottom: window.innerHeight - caja.top + 6 } : { top: caja.bottom + 6 }),
		});
	};

	return (
		<>
			<button type="button" className="waMenuBoton" aria-label="Más Acciones" aria-expanded={!!pos}
				onClick={(e) => { e.stopPropagation(); abrir(e.currentTarget); }}>
				<FontAwesomeIcon icon={solid("ellipsis")} />
			</button>
			{pos ? createPortal(
				<>
					<div className="waMenuFuera" onClick={(e) => { e.stopPropagation(); setPos(null); }} />
					<div className="waMenuPanel" style={pos}>
						{opciones.map((o) => (
							<button key={o.texto} type="button" className={o.peligro ? "peligro" : ""}
								onClick={(e) => { e.stopPropagation(); setPos(null); o.onClick(); }}>{o.texto}</button>
						))}
					</div>
				</>,
				document.body
			) : null}
		</>
	);
};

/** Confirmación de borrado: ventana chica, el motivo real del servidor si no se pudo. */
export const ConfirmarBorrado: React.FC<{
	titulo: string;
	nombre: string;
	onCancelar: () => void;
	onBorrar: () => Promise<void>;
	error: string | null;
}> = ({ titulo, nombre, onCancelar, onBorrar, error }) => {
	const [ocupado, setOcupado] = useState(false);
	return (
		<Ventana titulo={titulo} onClose={onCancelar} busy={ocupado} size="sm" pie={<>
			<button type="button" className="btn btn-outline-secondary" disabled={ocupado} onClick={onCancelar}>Cancelar</button>
			<button type="button" className="btn btn-primary" disabled={ocupado}
				onClick={async () => { setOcupado(true); try { await onBorrar(); } finally { setOcupado(false); } }}>Borrar</button>
		</>}>
			<div style={{ fontWeight: 600 }}>{nombre}</div>
			{error ? <div className="calAvisoOro" style={{ marginTop: 12, marginBottom: 0 }}>{error}</div> : null}
		</Ventana>
	);
};
